import { type ChangeEvent, type FC, useMemo } from "react";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { MultilevelSelect } from "~/common/components/Select/MultilevelSelect";
import { useTranslation } from "react-i18next";
import { Box, InputLabel, type SelectChangeEvent } from "~/common/mui";
import {
  PRICE_REFERENCE_EMPTY_OPTION,
  getGroupedPriceReferenceOptions,
  getPriceReferenceOptions,
} from "~/domains/priceReference/priceReferenceSelection";
import type { PriceReferenceSelection } from "~/domains/priceReference/types";

interface Props {
  className?: string;
  selected: string;
  onChange?: (newVal: string | undefined) => void;
  onBlur?: () => void;
  disabled?: boolean;
  highlight?: boolean;
  error?: boolean;
  helperLabel?: string;
  hideLabel?: boolean;
  externalLabel?: boolean;
  size?: "small" | "medium";
  selection?: PriceReferenceSelection;
}

const PriceReferenceDropdown: FC<Props> = ({
  onChange,
  onBlur,
  selected,
  hideLabel = false,
  error,
  helperLabel,
  className,
  externalLabel,
  highlight = false,
  size = "small",
  selection,
  ...props
}) => {
  const { t } = useTranslation();

  const flatValues = useMemo(() => (selection ? getPriceReferenceOptions(selection) : []), [selection]);
  const groupedValues = useMemo(() => (selection ? getGroupedPriceReferenceOptions(selection, t) : []), [selection, t]);

  if (!selection) {
    return null;
  }

  const onSelect = (e: SelectChangeEvent<unknown>) => {
    if (onChange) {
      let val = e.target.value as string;
      if (val === PRICE_REFERENCE_EMPTY_OPTION) {
        val = "";
      }
      onChange(val);
    }
  };
  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      let val = e.target.value as string;
      if (val === PRICE_REFERENCE_EMPTY_OPTION) {
        val = "";
      }
      onChange(val);
    }
  };

  const labelText = hideLabel ? undefined : t("component.dropdown.label.priceReference");
  const internalLabelText = externalLabel ? undefined : labelText;

  let cmp;

  const value = selected === "" ? "-" : selected;
  const isGrouped = selection.options.some((option) => option.groupCode);
  if (!isGrouped) {
    cmp = (
      <SimpleSelect
        label={internalLabelText}
        fullWidth={true}
        values={flatValues}
        value={value}
        onChange={handleOnChange}
        onBlur={onBlur}
        error={error}
        helperText={helperLabel}
        size={size}
        {...props}
      />
    );
  } else {
    cmp = (
      <MultilevelSelect
        label={internalLabelText}
        onBlur={onBlur}
        onChange={onSelect}
        value={value}
        list={groupedValues}
        variant={"outlined"}
        error={error}
        highlight={highlight}
        helperLabel={helperLabel}
        size={size}
        {...props}
      />
    );
  }

  if (externalLabel && labelText) {
    return (
      <Box>
        <InputLabel shrink>{labelText}</InputLabel>
        {cmp}
      </Box>
    );
  } else {
    return cmp;
  }
};
export default PriceReferenceDropdown;
